import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Screen, ScreenHeader } from "../components/AppShell";
import { Button, StateMessage } from "../components/ui";
import { LanguageToggle } from "../components/LanguageToggle";
import { PieceList } from "../features/pieces/PieceList";
import { ViewToggle } from "../features/pieces/ViewToggle";
import { PriceStrip } from "../features/prices/PriceStrip";
import { SearchBar } from "../features/filters/SearchBar";
import { FilterSheet } from "../features/filters/FilterSheet";
import { ActiveFilters } from "../features/filters/ActiveFilters";
import { AccessSheet } from "../features/access/AccessSheet";
import { useFilters } from "../features/filters/useFilters";
import { hasAnyFilter, activeFilterCount } from "../features/filters/filterState";
import { useFacets, useMe, usePieces } from "../api/queries";
import { useT } from "../i18n/strings";
import s from "../features/pieces/pieces.module.css";

export default function Home() {
  const t = useT();
  const navigate = useNavigate();
  const me = useMe();
  const { filters, patch, setFilters, clearFilters, clearAll } = useFilters();
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [accessOpen, setAccessOpen] = useState(false);

  const query = usePieces(filters);
  const facets = useFacets();

  const rows = query.data?.pages.flatMap((page) => page.results) ?? [];
  const count = activeFilterCount(filters);
  const filtering = hasAnyFilter(filters);

  const onSearch = useCallback((value: string) => patch({ q: value }), [patch]);
  const refresh = useCallback(() => void query.refetch(), [query]);

  return (
    <Screen>
      <ScreenHeader
        sticky
        actions={
          <>
            {me.data?.access?.can_manage_access && (
              <Button variant="ghost" icon="maker" onClick={() => setAccessOpen(true)}>
                {t.access.title}
              </Button>
            )}
            <ViewToggle />
            <LanguageToggle />
          </>
        }
      >
        <div className={s.masthead}>
          <h1 className={s.mastheadTitle}>{t.home.title}</h1>
          {/* /facets counts the whole tray, not the filtered slice, so it hides
              while filtering rather than contradict the rows underneath. */}
          {facets.data && !filtering && (
            <p className={s.mastheadCount}>
              <span className="mono">{facets.data.total}</span>{" "}
              {t.home.countSuffix(facets.data.total)}
            </p>
          )}
        </div>

        <PriceStrip />

        <SearchBar
          value={filters.q}
          onChange={onSearch}
          filterCount={count}
          onOpenFilters={() => setFiltersOpen(true)}
        />
        <ActiveFilters filters={filters} onChange={patch} onClearAll={clearFilters} />
      </ScreenHeader>

      <PieceList
        rows={rows}
        isLoading={query.isPending}
        error={query.error}
        hasMore={Boolean(query.hasNextPage)}
        isFetchingMore={query.isFetchingNextPage}
        onLoadMore={() => query.fetchNextPage()}
        onRefresh={refresh}
        empty={
          filtering ? (
            <StateMessage
              icon="search"
              title={t.home.noHitsTitle}
              body={t.home.noHitsBody}
              action={
                <Button variant="secondary" onClick={clearAll}>
                  {t.actions.clearAll}
                </Button>
              }
            />
          ) : (
            <StateMessage
              icon="pieces"
              title={t.home.emptyTitle}
              body={t.home.emptyBody}
              action={
                <Button variant="secondary" icon="add" onClick={() => navigate("/new")}>
                  {t.tabs.add}
                </Button>
              }
            />
          )
        }
      />

      <FilterSheet
        open={filtersOpen}
        onClose={() => setFiltersOpen(false)}
        filters={filters}
        onApply={(next) => setFilters(next)}
      />
      <AccessSheet open={accessOpen} onClose={() => setAccessOpen(false)} />
    </Screen>
  );
}
